import type { ElementType, ReactNode } from 'react';
import { Link } from 'react-router';
import type { PolymorphicProps } from '#pie/constants/interfaces';
import { getRelativeDate } from '#pie/i18n/dateFormats';
import { ConditionalWrapper } from '#pie/utils/ConditionalWrapper';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { tv } from '#pie/utils/TailwindUtils';
import { Image } from '../image/Image';
import { Skeleton } from '../skeleton/Skeleton';
import { SkeletonLines } from '../skeleton-lines/SkeletonLines';
import { Stack } from '../stack/Stack';
import { Text } from '../text/Text';

interface Props {
  title: string;
  description: ReactNode;
  imageUrl?: string;
  date: Date;
  isLoading?: false;
  className?: string;
}

interface LoadingProps extends Omit<Partial<Props>, 'isLoading'> {
  isLoading: true;
}

export type NewsCardProps<TComponent extends ElementType = typeof Link> =
  PolymorphicProps<TComponent, VariantProps<typeof newsCard>> &
    (Props | LoadingProps);

export function NewsCard<TComponent extends ElementType = typeof Link>({
  title,
  description,
  imageUrl,
  date,
  isLoading,
  as,
  to,
  className,
  ...props
}: NewsCardProps<TComponent>) {
  const style = newsCard();
  const Component = as ?? Link;
  const articleUrl = to?.replace(/^\/news/, '/nieuws');

  return (
    <ConditionalWrapper
      condition={!isLoading && to}
      wrapper={(children) => (
        <Component
          to={articleUrl}
          className={style.base({ className })}
          {...props}
        >
          {children}
        </Component>
      )}
      elseWrapper={(children) => (
        <div className={style.base({ className })}>{children}</div>
      )}
    >
      {isLoading ? (
        <Skeleton className={style.image()} />
      ) : (
        !!imageUrl && (
          <Image src={imageUrl} alt={title} className={style.image()} />
        )
      )}

      <Stack gap="sm">
        <Text as="h4" variant="h4" className={style.title()}>
          {isLoading ? <Skeleton /> : title}
        </Text>
        <Text className="line-clamp-3">
          {isLoading ? <SkeletonLines lines={3} /> : description}
        </Text>
      </Stack>

      <Stack gap="md" className="mt-auto">
        <Text as="time" variant="sm" className={style.date()} aria-label="date">
          {isLoading ? <Skeleton className="w-12" /> : getRelativeDate(date)}
        </Text>
      </Stack>
    </ConditionalWrapper>
  );
}

export const newsCard = tv({
  base: 'rounded-card-interactive relative bg-white p-6 pb-4 hover:shadow-card flex flex-col h-full gap-4',
  slots: {
    categories: 'flex flex-wrap items-center gap-2',
    image: 'rounded-xs aspect-[4/3] w-full object-cover',
    title: 'text-content-heading line-clamp-2 min-h-[3.5rem]',
    date: 'text-content-tertiary border-t pt-2 border-border-divider',
  },
});
